import { AUTH_ENABLED } from '../authConfig';

/**
 * AUTH-4 — the single cross-host-safe fetch wrapper used by every backend caller.
 *
 * When REACT_APP_AUTH_ENABLED is off (the default) this is a plain credentialed
 * fetch — byte-for-byte the previous behavior. When on, it attaches:
 *   - `X-Anonymous-Id` (always): a client-generated UUID persisted in localStorage.
 *     Per AUTH-12 the SameSite=Lax cookie does not survive the cross-host split, so
 *     the anonymous identity travels in this header instead. The server echoes the
 *     resolved id back (CORS-exposed) and we persist any change.
 *   - `Authorization: Bearer <token>` (when signed in): the Clerk session token,
 *     read from the global Clerk instance so callers need no React context.
 *
 * See docs/architecture/adr/adr-007-clerk-auth-and-tiers.md and
 * docs/investigations/auth-12-anonymous-cookie-baseline.md.
 */

const ANON_KEY = 'cc_anon_id';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function genUuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    // RFC 4122 v4 fallback for older/non-secure contexts.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

function getOrCreateAnonId() {
    try {
        let id = window.localStorage.getItem(ANON_KEY);
        if (!id || !UUID_RE.test(id)) {
            id = genUuid();
            window.localStorage.setItem(ANON_KEY, id);
        }
        return id;
    } catch (e) {
        // localStorage blocked (e.g. private mode): the server mints a per-request
        // id and echoes it; we simply cannot persist it. Anonymous still works.
        return null;
    }
}

async function getBearerToken() {
    try {
        const session = typeof window !== 'undefined' && window.Clerk && window.Clerk.session;
        if (session && typeof session.getToken === 'function') {
            return (await session.getToken()) || null;
        }
    } catch (e) {
        // Clerk not ready or no session — fall through to anonymous.
    }
    return null;
}

/**
 * Drop-in replacement for `fetch` against the backend API.
 * @param {string} url
 * @param {RequestInit} [options]
 * @returns {Promise<Response>}
 */
export async function apiFetch(url, options = {}) {
    if (!AUTH_ENABLED) {
        return fetch(url, { credentials: 'include', ...options });
    }

    const headers = { ...(options.headers || {}) };

    const anonId = getOrCreateAnonId();
    if (anonId) {
        headers['X-Anonymous-Id'] = anonId;
    }

    const token = await getBearerToken();
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const res = await fetch(url, { credentials: 'include', ...options, headers });

    // Persist a server-resolved/minted anonymous id so it stays stable next time.
    try {
        const echoed = res.headers.get('X-Anonymous-Id');
        if (echoed && UUID_RE.test(echoed) && echoed !== anonId) {
            window.localStorage.setItem(ANON_KEY, echoed);
        }
    } catch (e) {
        // ignore (storage blocked or opaque response)
    }

    return res;
}

export default apiFetch;
